import { useMarketplaceDashboard } from "@/api/hooks/marketplace";
import { copyMarketplaceDashboard } from "@/api/hooks/profile";
import { setTitle } from "@/effects";
import { MarketplaceDashboardDetailView } from "@/marketplace/MarketplaceDashboardDetailView";
import { buildAddDashboardErrorToast, buildAddDashboardSuccessToast, useMarketplaceToast } from "@/marketplace/toast";
import { useAppDispatch } from "@/reducer";
import { pageMarketplace, pageProfiles, startsWithExtraPath } from "@/route";
import { t } from "@lingui/core/macro";
import { useFlag } from "@openfeature/react-sdk";
import { useEffect, useState } from "react";
import { Redirect, useLocation, useParams } from "react-router-dom";

const ADDED_FEEDBACK_MS = 10_000;

type MarketplaceDashboardPageProps = {
  showCustomLogo?: boolean;
};

export default function MarketplaceDashboardPage({ showCustomLogo }: MarketplaceDashboardPageProps) {
  const { value: isMarketplaceEnabled } = useFlag("marketplace", false);
  const location = useLocation();
  const routePrefix = startsWithExtraPath(location.pathname);
  const profilesPath = `${routePrefix}${pageProfiles.asString()}`;
  const marketplaceHref = `${routePrefix}${pageMarketplace.asString()}`;
  const { id } = useParams<{ id: string }>();
  const profileId = Number(id);

  if (!isMarketplaceEnabled) {
    return <Redirect to={profilesPath} />;
  }

  return (
    <MarketplaceDashboardDetail
      profileId={profileId}
      marketplaceHref={marketplaceHref}
      routePrefix={routePrefix}
      showCustomLogo={showCustomLogo}
    />
  );
}

function MarketplaceDashboardDetail({
  profileId,
  marketplaceHref,
  routePrefix,
  showCustomLogo,
}: {
  profileId: number;
  marketplaceHref: string;
  routePrefix: string;
  showCustomLogo?: boolean;
}) {
  const dispatch = useAppDispatch();
  const { data: dashboard, isLoading, isError } = useMarketplaceDashboard(profileId);
  const { showToast, toast } = useMarketplaceToast();
  const [isCopying, setIsCopying] = useState(false);
  const [showAdded, setShowAdded] = useState(false);

  useEffect(() => {
    if (dashboard) {
      setTitle(dashboard.title);
      return;
    }
    setTitle(t({ id: "marketplace.title", message: "Marketplace" }));
  }, [dashboard]);

  useEffect(() => {
    if (!showAdded) {
      return;
    }

    const timeoutId = window.setTimeout(() => setShowAdded(false), ADDED_FEEDBACK_MS);
    return () => window.clearTimeout(timeoutId);
  }, [showAdded]);

  const handleAddDashboard = async () => {
    if (!dashboard || showAdded || isCopying) {
      return;
    }

    setIsCopying(true);
    try {
      const copiedProfile = await dispatch(copyMarketplaceDashboard(dashboard.id)).unwrap();
      setShowAdded(true);
      showToast(buildAddDashboardSuccessToast(dashboard.title, copiedProfile, routePrefix));
    } catch (error) {
      console.error("Failed to copy marketplace dashboard:", error);
      showToast(buildAddDashboardErrorToast(error instanceof Error ? error.message : undefined));
    } finally {
      setIsCopying(false);
    }
  };

  if (isError || (!isLoading && !dashboard)) {
    return <Redirect to={marketplaceHref} />;
  }

  if (isLoading || !dashboard) {
    return null;
  }

  return (
    <>
      <MarketplaceDashboardDetailView
        dashboard={dashboard}
        marketplaceHref={marketplaceHref}
        showCustomLogo={showCustomLogo}
        onAddDashboard={handleAddDashboard}
        isAdding={isCopying}
        showAdded={showAdded}
      />
      {toast}
    </>
  );
}
